// Connects a form's own lifecycle to the reload guard.
//
// These deliberately MIRROR state the form already keeps rather than
// introduce a second source of truth. A form that sets dirty=false only
// after a confirmed save, and saving=false in a finally, already encodes the
// rules correctly; duplicating that logic here would be a second place to
// get it wrong.
//
// What follows from mirroring:
//   a failed save leaves dirty true   -> the hold survives, as it must
//   a successful save clears dirty    -> the hold goes
//   a request settling either way     -> finally clears saving -> hold goes
import { useEffect } from 'react';
import { hold } from './dirtyRegistry';

/** Holds a reload while this form has unsaved edits. `label` is shown to the
 *  person: name the work ("imprest policy"), not the component. */
export const useUnsavedGuard = (dirty: boolean, id: string, label: string): void => {
  useEffect(() => {
    if (!dirty) return undefined;
    return hold({ id: `unsaved:${id}`, kind: 'unsaved', label });
  }, [dirty, id, label]);
};

/** Holds a reload while a request is in flight. Pending is the dangerous
 *  kind: the server may still apply it, so a reload can lose the answer
 *  while the action happens anyway. */
export const usePendingGuard = (pending: boolean, id: string, label: string): void => {
  useEffect(() => {
    if (!pending) return undefined;
    return hold({ id: `pending:${id}`, kind: 'pending', label });
  }, [pending, id, label]);
};
